import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { t } from '@/i18n';
import { PageLayout } from '@/components/layout/PageLayout';
import { SiteNow } from '@/components/arrivals/SiteNow';
import { ExpectedLoadings } from '@/components/arrivals/ExpectedLoadings';
import { UrgencyPanel } from '@/components/arrivals/UrgencyPanel';
import { UrgencyBar } from '@/components/arrivals/UrgencyBar';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { LoadingDetailSheet } from '@/components/arrivals/LoadingDetailSheet';
import { PlanImport } from '@/components/arrivals/PlanImport';
import { PlanSources } from '@/components/arrivals/PlanSources';
import { ActionQueue } from '@/components/arrivals/ActionQueue';
import { ActionBanner } from '@/components/arrivals/ActionBanner';
import { actionQueue } from '@/lib/actionQueue';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { stateStyles, stateLabel } from '@/components/arrivals/arrivalsUi';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useSubView } from '@/components/layout/subViews';
import { useUrgencySimulation } from '@/hooks/useUrgencySimulation';
import { loadingStates } from '@/data/arrivalsData';
import { cn } from '@/lib/utils';
import { Truck, FileUp } from 'lucide-react';
import { HelpTip } from '@/components/ui/help-tip';

export default function Kamiony() {
  const sim = useUrgencySimulation();
  const { loadings } = sim;
  const [selectedId, setSelectedId] = useState(null);
  const view = useSubView('/kamiony');
  const isDesktop = useIsDesktop();
  // These views need the full width; the others share it with the follow-up panel.
  const selected = loadings.find(l => l.id === selectedId) || null;
  const actionCount = useMemo(() => actionQueue(loadings).length, [loadings]);

  // Actions confirmed in the Ctrl+K assistant (#76): ?nakladka=K-01 opens a loading,
  // ?kontrola=1 runs the 13:00 check. The parameter is removed once handled.
  const [params, setParams] = useSearchParams();
  const { runCheck, phase } = sim;
  useEffect(() => {
    const id = params.get('nakladka');
    const check = params.get('kontrola') === '1';
    if (!id && !check) return;
    if (id) setSelectedId(id);
    if (check && phase === 'idle') runCheck();
    setParams({}, { replace: true });
  }, [params, setParams, runCheck, phase]);

  const stateCounts = useMemo(() => {
    const counts = Object.fromEntries(loadingStates.map(s => [s, 0]));
    loadings.forEach(l => { counts[l.state] += 1; });
    return counts;
  }, [loadings]);

  return (
    <PageLayout title={t('trucks.title')}>
      {/* State summary */}
      <div className="flex flex-wrap gap-2 mb-6" data-tour="arrivals-states">
        {loadingStates.filter(s => stateCounts[s] > 0 || s === 'no-info').map(s => (
          <Badge key={s} variant="outline" className={cn('gap-1.5 text-xs', stateStyles[s], s === 'no-info' && stateCounts[s] > 0 && 'ring-2 ring-red-300')}>
            {stateLabel(s)}: <span className="font-bold">{stateCounts[s]}</span>
          </Badge>
        ))}
        <HelpTip id="states" />
        <Badge variant="outline" className="gap-1.5 text-xs ml-auto">
          <Truck className="h-3 w-3" /> {t('arrivals.plannedToday')}: <span className="font-bold">{loadings.length}</span>
        </Badge>
        {view === 'today' && (
          <Button asChild variant="outline" size="sm" className="h-6 gap-1 text-xs">
            <Link to="/kamiony/import"><FileUp className="h-3 w-3" /> {t('import.title')}</Link>
          </Button>
        )}
      </div>

      {view === 'import' && <PlanImport loadings={loadings} onImport={sim.importLoadings} />}
      {view === 'sources' && <PlanSources />}

      {/* Day overview: what is happening. Acting on it happens in the Akce view. */}
      {view === 'today' && (
        <div className="space-y-4">
          <ActionBanner count={actionCount} />
          <ExpectedLoadings loadings={loadings} onSelect={(l) => setSelectedId(l.id)} />
        </div>
      )}

      {/* Akce: the work queue next to the automatic 13:00 follow-up and its live log. */}
      {view === 'actions' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 min-w-0 space-y-4">
            {!isDesktop && <UrgencyBar sim={sim} onSelect={(l) => setSelectedId(l.id)} />}
            <ActionQueue sim={sim} onOpen={setSelectedId} />
          </div>
          {isDesktop && (
            <div className="lg:col-span-2 min-w-0">
              <UrgencyPanel sim={sim} onSelect={(l) => setSelectedId(l.id)} />
            </div>
          )}
        </div>
      )}

      {view === 'map' && (
        <SiteNow loadings={loadings} clock={sim.clock} onSelect={(l) => setSelectedId(l.id)} />
      )}

      <LoadingDetailSheet
        loading={selected}
        loadings={loadings}
        onClose={() => setSelectedId(null)}
        onAnnounced={sim.syncAnnouncements}
        onReply={sim.applyReply}
        onReplan={sim.applyPlan}
        onDocument={sim.attachDocument}
      />
    </PageLayout>
  );
}
